import { useState, useEffect, useCallback, useRef } from "react"
import cable from "@/lib/cable"

type RenderStep = "preparing" | "rendering" | "finalising"

type RenderEvent = {
  event: "render_progress"
  render_id: string
  step: RenderStep
  status: "in_progress" | "completed" | "failed"
  detail?: string
  progress?: number
  data?: {
    video_url?: string
    render_id?: string
  }
}

type RenderProgress = {
  step: RenderStep
  status: "in_progress" | "completed" | "failed"
  detail: string
  progress: number
  video_url?: string
}

const INITIAL_STATE: RenderProgress = {
  step: "preparing",
  status: "in_progress",
  detail: "Starting render...",
  progress: 0,
}

const POLL_INTERVAL_MS = 5000

export function useRenderChannel(
  videoId: string | number,
  renderId: string | null,
  onComplete?: () => void,
) {
  const [renderProgress, setRenderProgress] = useState<RenderProgress>(INITIAL_STATE)
  const completedRef = useRef(false)

  // Reset to initial state when renderId changes (set state during render pattern)
  const [prevRenderId, setPrevRenderId] = useState(renderId)
  if (renderId !== prevRenderId) {
    setPrevRenderId(renderId)
    if (renderId) {
      setRenderProgress(INITIAL_STATE)
    }
  }

  const reset = useCallback(() => {
    setRenderProgress(INITIAL_STATE)
    completedRef.current = false
  }, [])

  useEffect(() => {
    if (!renderId) return

    completedRef.current = false

    const subscription = cable.subscriptions.create(
      { channel: "RenderChannel", id: renderId },
      {
        received(data: RenderEvent) {
          if (data.event !== "render_progress") return

          setRenderProgress({
            step: data.step,
            status: data.status,
            detail: data.detail || "",
            progress: data.progress ?? (data.step === "preparing" ? 0 : data.step === "finalising" ? 100 : 50),
            video_url: data.data?.video_url,
          })

          if (data.status === "completed" && !completedRef.current) {
            completedRef.current = true
            // Delay so the user can see the "Video ready!" state before reload
            setTimeout(() => {
              onComplete?.()
            }, 2500)
          }
        },
      },
    )

    // Polling fallback — catches terminal states if WebSocket drops
    const pollInterval = setInterval(async () => {
      if (completedRef.current) return

      try {
        const res = await fetch(`/dashboard/videos/${videoId}/renders/${renderId}`, {
          headers: { Accept: "application/json" },
        })
        if (!res.ok) return

        const data = await res.json()

        if (data.status === "completed") {
          setRenderProgress({
            step: "finalising",
            status: "completed",
            detail: "Video ready!",
            progress: 100,
            video_url: data.video_url,
          })
          if (!completedRef.current) {
            completedRef.current = true
            setTimeout(() => {
              onComplete?.()
            }, 2500)
          }
        } else if (data.status === "failed" || data.status === "timed_out") {
          setRenderProgress({
            step: "rendering",
            status: "failed",
            detail: data.error || "Render failed",
            progress: 0,
          })
          completedRef.current = true
        } else if (data.status === "rendering") {
          // Only update if WS hasn't already provided more granular progress
          setRenderProgress((prev) => {
            if (prev.step === "rendering" && prev.progress > 0) return prev
            return {
              step: "rendering",
              status: "in_progress",
              detail: prev.detail || "Rendering...",
              progress: prev.progress || 0,
            }
          })
        }
        // "pending" — no update, still starting
      } catch {
        // Network error — silently retry on next interval
      }
    }, POLL_INTERVAL_MS)

    return () => {
      subscription.unsubscribe()
      clearInterval(pollInterval)
    }
  }, [videoId, renderId, onComplete])

  return { renderProgress, reset }
}
